import { Flex, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useExperimentsState } from '~/atoms/ExperimentsState'
import { useUserState } from '~/atoms/UserState'
import { ResponsivePage } from '~/components/layout/ResponsivePage'
import { GroupedScrollingList } from '~/components/shared/GroupedScrollingList'
import { GroupedScrollingListHeader } from '~/components/shared/GroupedScrollingListGroupHeader'
import { ApplicationRoutes } from '~/hooks/useRouting'
import {
  DateRange,
  SortDirection,
  useDateRangeWithSearchParams,
} from '~/types/Shared'

import AgentStatusDateHeader from './CallQueue/AgentStatusDateHeader'
import AgentStatusFilters from './CallQueue/AgentStatusFilters'
import AgentStatusTable from './CallQueue/AgentStatusTable'
import EventLogFilters from './CallQueue/EventLogFilters'
import EventLogTable from './CallQueue/EventLogTable'
import CallVolumeDashboard from './CallReporting/CallVolume/CallVolumeDashboard'
import CallVolumeFilters from './CallReporting/CallVolume/CallVolumeFilters'
import HeatMapDashboard from './CallReporting/HeatMap/HeatMapDashboard'
import HeatMapFilters from './CallReporting/HeatMap/HeatMapFilters'
import InboundAnswerRateDashboard from './CallReporting/InboundAnswerRate/InboundAnswerRateDashboard'
import InboundAnswerRateFilters from './CallReporting/InboundAnswerRate/InboundAnswerRateFilters'
import { HeatmapDirection } from './CallReporting/Shared/HeatmapDirectionDropdown'
import { HeatmapTimeRange } from './CallReporting/Shared/HeatmapTimeRangeDropdown'

export type DashboardSlug =
  | 'agent-status'
  | 'event-log'
  | 'inbound-answer-rate'
  | 'call-volume'
  | 'heat-map'

type DashboardItem = {
  name: string
  slug: DashboardSlug
}

export const DashboardsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [sharedDateRange, setSharedDateRange] = useDateRangeWithSearchParams()
  const parsedDirection =
    searchParams?.get('direction') ?? ('answered' as HeatmapDirection)
  const parsedTimeHours =
    searchParams?.get('time_hours') ?? ('daytime-hours' as HeatmapTimeRange)
  const createdAtSort = searchParams.get('createdAtSort') as
    | SortDirection
    | undefined

  const [eventLogFilters, setEventLogFilters] = useState<{
    eventTypes: string[]
    details: string[]
  }>({
    eventTypes: [],
    details: [],
  })
  const [eventLogDateTimeRange, setEventLogDateTimeRange] = useState<DateRange>(
    {
      before: undefined,
      after: undefined,
    },
  )

  const { experiments } = useExperimentsState()
  const dashboardsEnabled =
    experiments.find((experiment) => experiment.title === 'Dashboards')
      ?.enabled || false

  const { agentStatusesEnabled: callCenterEnabled } = useUserState()

  const navigate = useNavigate()

  const { slug } = useParams()
  const selectedDashboardSlug = slug as DashboardSlug

  const callCenterItems: DashboardItem[] = callCenterEnabled
    ? [
        { name: 'Agent Status', slug: 'agent-status' },
        { name: 'Event Log', slug: 'event-log' },
      ]
    : []

  const callDashboardsItems: DashboardItem[] = dashboardsEnabled
    ? [
        { name: 'Inbound Answer Rate', slug: 'inbound-answer-rate' },
        { name: 'Call Volume', slug: 'call-volume' },
        { name: 'Heat Map', slug: 'heat-map' },
      ]
    : []

  const dashboardGroups = new Map<string, DashboardItem[]>()
  if (callCenterItems.length) {
    dashboardGroups.set('Call Center', callCenterItems)
  }
  if (callDashboardsItems.length) {
    dashboardGroups.set('Call Dashboards', callDashboardsItems)
  }

  const handleBuildGroupContent = (groupIndex: number, groupName: string) => (
    <GroupedScrollingListHeader mb="0.5rem" index={groupIndex}>
      {groupName}
    </GroupedScrollingListHeader>
  )

  const handleBuildItemContent = (
    flatIndex: number,
    item: DashboardItem,
    isSelected: boolean,
  ) => (
    <Flex
      padding="0rem 0.75rem"
      height="3.25rem"
      width="100%"
      alignItems="center"
      borderRadius="0.5rem"
      cursor="pointer"
      bg={isSelected ? 'gray.400' : 'transparent'}
      _hover={{ bg: 'gray.400' }}
      marginBottom="0.5rem"
      onClick={() => handleItemClick(item.slug)}
    >
      <Text
        fontSize="1rem"
        fontWeight="400"
        lineHeight="1.5rem"
        overflow="hidden"
        textOverflow="ellipsis"
        color="gray.1300"
      >
        {item.name}
      </Text>
    </Flex>
  )

  const handleItemClick = (slug: DashboardSlug) => {
    navigate(`${ApplicationRoutes.DASHBOARDS}/${slug}`)
  }
  const handleDirectionChange = (dir: HeatmapDirection) => {
    searchParams.set('direction', dir)
    setSearchParams(searchParams)
  }
  const handleTimeRangeChange = (timeRange: HeatmapTimeRange) => {
    searchParams.set('time_hours', timeRange)
    setSearchParams(searchParams)
  }
  const allItems: DashboardItem[] = []
  dashboardGroups.forEach((items) => allItems.push(...items))
  const selectedItemIndex = allItems.findIndex(
    (item) => item.slug === selectedDashboardSlug,
  )

  const renderHeaderPanel = () => {
    if (!callCenterEnabled && !dashboardsEnabled) {
      return <Flex />
    }
    switch (selectedDashboardSlug) {
      case 'agent-status':
        return <AgentStatusFilters />
      case 'event-log':
        return (
          <EventLogFilters
            onFilterChangesApplied={setEventLogFilters}
            onDateTimeRangeChange={setEventLogDateTimeRange}
          />
        )
      case 'inbound-answer-rate':
        return (
          <InboundAnswerRateFilters
            dateTimeRange={sharedDateRange}
            onValueChange={setSharedDateRange}
          />
        )
      case 'call-volume':
        return (
          <CallVolumeFilters
            dateTimeRange={sharedDateRange}
            onValueChange={setSharedDateRange}
          />
        )
      case 'heat-map':
        return (
          <HeatMapFilters
            dateTimeRange={sharedDateRange}
            onValueChange={setSharedDateRange}
            onDirectionChange={handleDirectionChange}
            onTimeRangeChange={handleTimeRangeChange}
            direction={(parsedDirection as 'inbound' | 'outbound') || 'inbound'}
            timeRange={(parsedTimeHours as HeatmapTimeRange) || 'daytime-hours'}
          />
        )
      default:
        return <Flex />
    }
  }

  const renderDetailPanel = () => {
    if (!callCenterEnabled && !dashboardsEnabled) {
      return (
        <Flex justifyContent="center" alignItems="center" height="100%">
          <Text>No data available</Text>
        </Flex>
      )
    }
    switch (selectedDashboardSlug) {
      case 'agent-status':
        return (
          <>
            <AgentStatusDateHeader />
            <AgentStatusTable />
          </>
        )
      case 'event-log':
        return (
          <EventLogTable
            filters={eventLogFilters}
            dateTimeRange={eventLogDateTimeRange}
            initialCreatedAtSort={createdAtSort ?? SortDirection.Descending}
          />
        )
      case 'inbound-answer-rate':
        return <InboundAnswerRateDashboard sharedDateRange={sharedDateRange} />
      case 'call-volume':
        return <CallVolumeDashboard sharedDateRange={sharedDateRange} />
      case 'heat-map':
        return (
          <HeatMapDashboard
            sharedDateRange={sharedDateRange}
            timeRangeMode={parsedTimeHours as HeatmapTimeRange}
            direction={parsedDirection as HeatmapDirection}
          />
        )
      default:
        return (
          <Flex justifyContent="center" alignItems="center" height="100%">
            <Text>No data available</Text>
          </Flex>
        )
    }
  }

  useEffect(() => {
    if (!slug) {
      const _defaultSlug: DashboardSlug =
        callCenterEnabled && dashboardsEnabled
          ? 'agent-status'
          : callCenterEnabled
            ? 'agent-status'
            : dashboardsEnabled
              ? 'inbound-answer-rate'
              : 'agent-status'
      navigate(`${ApplicationRoutes.DASHBOARDS}/${_defaultSlug}`)
    }
  }, [slug])

  return (
    <ResponsivePage
      header={renderHeaderPanel()}
      listPanel={
        <Flex
          width="22rem"
          height="100%"
          flexDirection="column"
          alignSelf="stretch"
          borderRight="2px solid var(--Gray-Gray400, #E5E5EE)"
          background="var(--Gray-Gray200, #F6F6FA)"
          paddingLeft="0.5rem"
        >
          <GroupedScrollingList
            sx={{
              padding: '0.75rem 0rem',
              alignItems: 'center',
              gap: '0.25rem',
              flex: '1 0 0',
              flexDirection: 'column',
              alignSelf: 'stretch',
            }}
            groupItemData={dashboardGroups}
            selectedItemIndex={selectedItemIndex}
            scrollbarOptions={{ showOnHoverOnly: true }}
            onBuildGroupContent={handleBuildGroupContent}
            onBuildItemContent={handleBuildItemContent}
            showLoaderFooter={false}
          />
        </Flex>
      }
      detailPanel={
        <Flex
          padding="1.25rem"
          flexDirection="column"
          alignItems="center"
          gap="0.25rem"
          flex="1 0 0"
          alignSelf="stretch"
          overflow="auto"
        >
          {renderDetailPanel()}
        </Flex>
      }
    />
  )
}
